import { ProductRegistrationAvailability } from "@/admin/experiences/create/__generated__/useCreateExperienceFormMutation.graphql"
import { ProductType } from "@/admin/pathways/__generated__/DeletePathwayButton_productFragment.graphql"
import { useLabel } from "@/core/context/LabelsContext"
import { useUnsavedChangesModalContext } from "@/core/context/UnsavedChangesModalProvider"
import FormStore from "@/core/form/store/FormStore"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import { ExperienceSettingsAccessSelect_ProductFragment$key } from "@/product/settings/__generated__/ExperienceSettingsAccessSelect_ProductFragment.graphql"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import ProductUtils from "@/product/util/productUtils"
import {
  DiscoFormControl,
  DiscoIcon,
  DiscoSelect,
  DiscoText,
  DiscoTextButton,
} from "@disco-ui"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"
import { generatePath, useHistory } from "react-router-dom"
import classes from "./ExperienceSettingsAccessSelect.module.css"

const REGISTRATION_AVAILABILITY_OPTIONS: ProductRegistrationAvailability[] = [
  "public",
  "private",
  "hidden",
]

interface Props extends TestIDProps {
  productType?: Extract<ProductType, "course" | "pathway">
  title?: string
  sublabel?: string
  form: FormStore<{ registrationAvailability?: ProductRegistrationAvailability | null }>
  productKey?: ExperienceSettingsAccessSelect_ProductFragment$key | null
}

function ExperienceSettingsAccessSelect({
  form,
  productType = "course",
  productKey,
  title,
  sublabel,
  testid = "ExperienceSettingsAccessSelectFormFields",
}: Props) {
  const membersLabel = useLabel("admin_member")
  const productLabel = useAdminProductLabel(productType)
  const organizationLabel = useLabel("organization")

  const history = useHistory()
  const { handleLeave } = useUnsavedChangesModalContext()

  const product = useFragment<ExperienceSettingsAccessSelect_ProductFragment$key>(
    graphql`
      fragment ExperienceSettingsAccessSelect_ProductFragment on Product {
        slug
      }
    `,
    productKey ?? null
  )

  const isInviteOnly = form.state.registrationAvailability === "hidden"
  const showInviteOnlySubtext = Boolean(isInviteOnly && product)

  return (
    <>
      <DiscoFormControl
        title={title}
        sublabel={sublabel}
        marginBottom={showInviteOnlySubtext ? 2 : 2.5}
      >
        <DiscoSelect
          testid={`${testid}.registration-availability`}
          autoComplete={false}
          value={form.state.registrationAvailability}
          onChange={(v) => {
            form.state.registrationAvailability = v as ProductRegistrationAvailability
          }}
          customSelectProps={{
            menuItemClassName: classes.menuItem,
            MenuProps: {
              classes: {
                paper: classes.menuList,
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "right",
              },
            },
          }}
          options={REGISTRATION_AVAILABILITY_OPTIONS.map((v) => ({ value: v, title: v }))}
          disableClearable
          renderOption={({ value }) => {
            const availabilityLabel = ProductUtils.getAvailabilityLabel(
              value,
              membersLabel
            )
            const icon = ProductUtils.getAvailabilityIcon(value)
            const subtitle = ProductUtils.getAvailabilitySubtitle(
              value,
              membersLabel,
              productLabel,
              organizationLabel
            )

            return (
              <div className={classes.option}>
                <DiscoIcon className={classes.optionIcon} icon={icon} />
                <div className={classes.optionText}>
                  <DiscoText variant={"body-md-600"}>{availabilityLabel}</DiscoText>
                  <DiscoText variant={"body-md"} color={"text.secondary"}>
                    {subtitle}
                  </DiscoText>
                </div>
              </div>
            )
          }}
          renderValue={({ value }) => {
            const availabilityLabel = ProductUtils.getAvailabilityLabel(
              value,
              membersLabel
            )
            return <DiscoText variant={"body-sm"}>{availabilityLabel}</DiscoText>
          }}
        />
      </DiscoFormControl>
      {showInviteOnlySubtext && product && (
        <DiscoText variant={"body-sm"} color={"text.secondary"} marginBottom={2.5}>
          {`To view invites and invite new ${membersLabel.plural} to this ${productLabel.singular}, go to `}
          <DiscoTextButton
            onClick={handleGoToInvites}
            textVariant={"body-sm-600"}
            className={classes.inviteLink}
          >
            {`${membersLabel.plural} > Invites`}
          </DiscoTextButton>
        </DiscoText>
      )}
    </>
  )

  function handleGoToInvites() {
    if (!product) return

    handleLeave({
      onLeave: () => {
        history.push(
          generatePath(ROUTE_NAMES.PRODUCT.MEMBERS.INVITES, {
            productSlug: product.slug,
          })
        )
      },
    })
  }
}

export default observer(ExperienceSettingsAccessSelect)
